// src/features/apps/legal/PrivacyTemplate.ts
//
// The customer privacy-policy generator (owner-approved 2026-09-11; build spec
// ring2-readiness §5). Pure: inputs + derived facts + a date + the operator's
// brand name → a structured document. The rendered document is what gets
// ADOPTED and stored server-side, byte for byte; a template fix is a new
// TEMPLATE_VERSION plus a "review and re-adopt" prompt, never a silent
// re-render of an adopted text. Nothing here reads the DOM or the network.

import type { AppCfgsDto } from '@/lib/types/App';
import type { PrivacyDoc, PrivacyInputs } from '@/lib/types/AppLegal';

/**
 * Bumped whenever the rendered text changes for the same inputs, or when an
 * adopted document may carry a defect the template has since fixed — an
 * adoption on an older version reads "out of date" until it is re-adopted.
 * 2026-09-17.1: the operator sentence named the account's own reseller label
 * for reseller root accounts (the operator rule now lives in help/brandTokens).
 */
export const PRIVACY_TEMPLATE_VERSION = '2026-09-17.1';

/** Why an adopted policy needs another look; 'current' when it does not. */
export type PrivacyStaleness = 'current' | 'facts' | 'template';

/** The Status chip for the privacy policy, derived from the listing URL + the adoption record. */
export interface PrivacyReadiness {
  kind: 'missing' | 'custom' | 'adopted' | 'out_of_date';
  label: string;
  color: string;
  /** The button beside the chip. */
  action: 'Write it' | 'Review';
  staleness: PrivacyStaleness;
}

/** The platform facts the policy describes; recomputed from the live app on every render. */
export interface PrivacyDerivedFacts {
  /** The app's names per platform (the store listing name family). */
  appNames: string[];
  /** Platforms the app is published on (roku | web | ...). */
  platforms: string[];
  /** A Roku ad tag is configured — ad partners receive device ad identifiers. */
  adsEnabled: boolean;
  /** The app is directed at children — viewer analytics run in anonymous mode. */
  kidsDirected: boolean;
}

/** What the customer types once; `fingerprint` is added at render time. */
export type PrivacyInputDraft = Omit<PrivacyInputs, 'fingerprint'>;

export class PrivacyTemplate {
  static readonly DEFAULT_COUNTRIES = 'every country where the app store offers the app';

  /** The facts the policy states, read from the app the way the shells see it. */
  static derivedFacts(app: AppCfgsDto): PrivacyDerivedFacts {
    const names = new Set<string>();
    const orgName = app.appCfg.orgInfo.name.trim();
    if (orgName) names.add(orgName);
    const rokuTitle = app.roku?.core.title.trim();
    if (rokuTitle) names.add(rokuTitle);
    const platforms: string[] = [];
    if (app.roku) platforms.push('roku');
    if (app.web) platforms.push('web');
    return {
      appNames: [...names],
      platforms,
      adsEnabled: !!app.roku?.core.adUrl?.trim(),
      kidsDirected: app.appCfg.core.isChildTargeted,
    };
  }

  /**
   * A short stable hash of the derived facts (order-independent lists), so the
   * Status tab can tell "the policy was rendered for different facts" without
   * storing the facts themselves.
   */
  static fingerprint(facts: PrivacyDerivedFacts): string {
    const canonical = JSON.stringify({
      appNames: [...facts.appNames].sort(),
      platforms: [...facts.platforms].sort(),
      adsEnabled: facts.adsEnabled,
      kidsDirected: facts.kidsDirected,
    });
    // FNV-1a 32-bit — deterministic, dependency-free, plenty for a change detector.
    let h = 0x811c9dc5;
    for (let i = 0; i < canonical.length; i++) {
      h ^= canonical.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  }

  /** True when the adopted version was rendered for facts that have since changed. */
  static isOutOfDate(adoptedFingerprint: string, facts: PrivacyDerivedFacts): boolean {
    return adoptedFingerprint !== PrivacyTemplate.fingerprint(facts);
  }

  /** True when the adopted version was rendered by an earlier template (its text may carry a since-fixed defect). */
  static isTemplateOutdated(adoptedTemplateVersion: string): boolean {
    return adoptedTemplateVersion !== PRIVACY_TEMPLATE_VERSION;
  }

  /** Why the adopted version needs another look — the template first (a text fix), then the facts. */
  static staleness(
    adopted: { templateVersion: string; inputsFingerprint: string },
    facts: PrivacyDerivedFacts,
  ): PrivacyStaleness {
    if (PrivacyTemplate.isTemplateOutdated(adopted.templateVersion)) return 'template';
    if (PrivacyTemplate.isOutOfDate(adopted.inputsFingerprint, facts)) return 'facts';
    return 'current';
  }

  /**
   * The Readiness card's privacy-policy chip. A store-listing Privacy Policy URL the customer
   * set by hand (or left from before the generator) is a policy THEY host:
   * "Custom URL", never "Missing" — and it stays custom when it differs from
   * the adopted version's published address, because the listing is what the
   * stores see. Without one: missing until adopted, then adopted or out of
   * date (template fix or facts drift).
   */
  static readiness(
    listingPrivacyUrl: string | null | undefined,
    adopted: { templateVersion: string; inputsFingerprint: string; effectiveDate: string; privacyPolicyUrl: string } | null,
    facts: PrivacyDerivedFacts,
  ): PrivacyReadiness {
    const custom = (listingPrivacyUrl ?? '').trim();
    if (custom && (!adopted || custom !== adopted.privacyPolicyUrl)) {
      return { kind: 'custom', label: 'Custom URL', color: 'blue', action: 'Review', staleness: 'current' };
    }
    if (!adopted) {
      return { kind: 'missing', label: 'Missing', color: 'orange', action: 'Write it', staleness: 'current' };
    }
    const staleness = PrivacyTemplate.staleness(adopted, facts);
    if (staleness !== 'current') {
      return { kind: 'out_of_date', label: 'Out of date', color: 'orange', action: 'Review', staleness };
    }
    return { kind: 'adopted', label: `Adopted ${adopted.effectiveDate}`, color: 'green', action: 'Review', staleness };
  }

  /** Default inputs: the support email from the store listing, everything else blank. */
  static defaultDraft(app: AppCfgsDto): PrivacyInputDraft {
    return {
      legalName: app.appCfg.orgInfo.name.trim(),
      address: '',
      contactEmail: app.listing?.supportEmail?.trim() ?? '',
      jurisdiction: '',
      countries: PrivacyTemplate.DEFAULT_COUNTRIES,
    };
  }

  /** `YYYY-MM-DD` in the browser's local day (the effective date the customer sees). */
  static todayIso(now: Date = new Date()): string {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /**
   * Render the policy. `operatorName` is the platform operator's brand as the
   * customer's account sees it (the direct brand, or the reseller's label) —
   * frozen into the document at adoption.
   */
  static render(
    draft: PrivacyInputDraft,
    facts: PrivacyDerivedFacts,
    effectiveDate: string,
    operatorName: string,
  ): PrivacyDoc {
    const legalName = draft.legalName.trim() || 'the publisher';
    const contact = draft.contactEmail.trim();
    const address = draft.address.trim();
    const jurisdiction = draft.jurisdiction.trim();
    const countries = draft.countries.trim() || PrivacyTemplate.DEFAULT_COUNTRIES;
    const appNames = facts.appNames.length > 0 ? facts.appNames : [legalName];
    const appList = PrivacyTemplate.joinNames(appNames);
    const platformList = PrivacyTemplate.platformSentence(facts.platforms);

    const sections: PrivacyDoc['sections'] = [];

    sections.push({
      heading: 'Who we are',
      paragraphs: [
        `${legalName} ("we", "us") publishes the ${appList} app${appNames.length > 1 ? 's' : ''}${platformList}. This policy explains what information the app collects, how it is used, and the choices you have.`,
        [
          address ? `Our address: ${address}.` : '',
          contact ? `Questions about this policy: ${contact}.` : '',
        ]
          .filter(Boolean)
          .join(' ') || 'Contact details are listed at the end of this policy.',
        `${operatorName} operates the video platform that delivers the app on our behalf and processes viewer information only under our instructions and only for the purposes described here.`,
      ],
    });

    const collect: string[] = [
      'The app does not ask you to create an account and does not collect your name, email address, or payment details. It collects the technical information needed to play video and to measure the audience:',
      'A random viewer identifier generated on your device. It is not linked to your name or to identifiers from other apps.',
      'Your approximate location (country and region), derived from the network address of your connection. The network address itself is not stored with your viewing history.',
      'Playback events: which titles were played, when, and for how long, plus the device type and app version, so that the app works and we can see what our audience watches.',
    ];
    if (facts.kidsDirected) {
      collect.push(
        'Because this app is directed to children, audience measurement runs in anonymous mode: the viewer identifier is not kept between sessions and no behavioral profile is built.',
      );
    }
    sections.push({ heading: 'Information the app collects', paragraphs: collect });

    sections.push({
      heading: 'How we use it',
      paragraphs: [
        'To deliver the video you asked for and remember where you left off.',
        'To measure our audience in aggregate: how many viewers, which titles, and from which regions.',
        'To keep the service reliable and secure and to investigate abuse.',
        'To meet legal obligations and respond to lawful requests.',
      ],
    });

    sections.push({
      heading: 'Advertising',
      paragraphs: facts.adsEnabled
        ? [
            'The app shows advertising. When an ad plays, the advertising partner receives your device advertising identifier and the technical details needed to serve and count the ad, subject to your device\'s advertising settings. You can limit ad tracking or reset the advertising identifier in your device settings.',
            'We do not sell your personal information. Advertising partners are not permitted to use what they receive for any purpose other than serving and measuring ads in the app.',
          ]
        : [
            'The app does not show third-party advertising and shares no information with advertising partners.',
          ],
    });

    const sharing: string[] = [
      'We do not sell your personal information.',
      `We share information only with ${operatorName}, which hosts and delivers the app as our service provider, and with advertising partners when ads are shown, as described above.`,
      'We may disclose information when the law requires it, to protect our rights, or as part of a merger or sale of our organization, in which case this policy continues to apply.',
    ];
    if (!facts.adsEnabled) {
      sharing.splice(1, 1, `We share information only with ${operatorName}, which hosts and delivers the app as our service provider.`);
    }
    sections.push({ heading: 'Sharing', paragraphs: sharing });

    sections.push({
      heading: 'Security and retention',
      paragraphs: [
        'Information travels between your device and the platform over encrypted connections. Access is limited to the people and systems that need it to run the service.',
        'Playback events are kept for as long as needed to produce audience reports, then aggregated or deleted. Aggregated statistics do not identify you.',
      ],
    });

    sections.push({
      heading: 'Children',
      paragraphs: facts.kidsDirected
        ? [
            'This app is directed to children. It does not collect personal information from children beyond the anonymous technical information described above, does not show behavioral advertising, and does not build profiles of viewers.',
          ]
        : [
            'This app is not directed to children under 13, and we do not knowingly collect personal information from children. If you believe a child has provided us personal information, contact us and we will delete it.',
          ],
    });

    sections.push({
      heading: 'Your choices and rights',
      paragraphs: [
        `You can ask us to delete the information associated with your viewer identifier, or to tell you what we hold, by contacting us${contact ? ` at ${contact}` : ''}. We answer within the time the law allows.`,
        'California residents have the right to know what personal information we collect and how it is used and shared, to request its deletion, and not to be discriminated against for exercising these rights. We do not sell personal information and have not sold it in the past twelve months.',
        'If you are in the European Economic Area, the United Kingdom, or another jurisdiction with data-protection law, you may have the rights to access, correct, delete, restrict or object to the processing of your information, and to data portability. We process the information described here on the basis of our legitimate interest in delivering the app and understanding our audience. You may lodge a complaint with your local supervisory authority.',
        `The app is offered in ${countries}. Information may be processed in the United States and in other countries where the platform operates, under safeguards required by law.`,
      ],
    });

    sections.push({
      heading: 'Changes to this policy',
      paragraphs: [
        `This policy is effective as of ${PrivacyTemplate.longDate(effectiveDate)}. When it changes, the new version is posted at this address with a new effective date.`,
      ],
    });

    sections.push({
      heading: 'Contact',
      paragraphs: [
        [
          legalName,
          address,
          contact ? `Email: ${contact}` : '',
          jurisdiction ? `Governing law: ${jurisdiction}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      ],
    });

    return { title: `Privacy Policy — ${appNames[0]}`, sections };
  }

  // ── helpers ──

  private static joinNames(names: string[]): string {
    if (names.length <= 1) return names[0] ?? '';
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  }

  private static platformSentence(platforms: string[]): string {
    const labels: Record<string, string> = {
      roku: 'Roku',
      web: 'the web',
      firetv: 'Fire TV',
      appletv: 'Apple TV',
      googletv: 'Google TV',
      androidtv: 'Android TV',
      android: 'Android',
      ios: 'iOS',
    };
    const list = platforms.map((p) => labels[p] ?? p);
    if (list.length === 0) return '';
    return ` on ${PrivacyTemplate.joinNames(list)}`;
  }

  /** `2026-09-16` → `September 16, 2026` without touching the locale (the stored text must not vary by browser). */
  private static longDate(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return iso;
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    const month = months[Number(m[2]) - 1] ?? m[2];
    return `${month} ${Number(m[3])}, ${m[1]}`;
  }
}
